import { FC, useMemo, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { usePostHog } from '@posthog/react';
import { Check, ChevronsUpDown, Minus, Plus, Search } from 'lucide-react';
import ClassBadge from '../ClassIcon/ClassBadge';
import { hidePanelUntilPlaced, openPanelBesideNav } from './openPanelBesideNav';
import { GlobalFilterClass, useGlobalClassFilter } from '../../custom-hooks/useGlobalClassFilter';
import { getClassChangeType, getClassSelectionScope } from '../../experiments/TEAMF-824/classSelectionScope';

const classLabel = (cls: GlobalFilterClass): string => (cls.section ? `${cls.name} · ${cls.section}` : cls.name);

const studentsLabel = (count: number): string => `${count} ${count === 1 ? 'student' : 'students'}`;

const GlobalClassFilter: FC = () => {
  const { classes, selectedClasses, selectedStudentCount, select } = useGlobalClassFilter();
  const overlay = useRef<OverlayPanel>(null);
  const posthog = usePostHog();
  const openedWith = useRef<string[]>([]);
  const [search, setSearch] = useState('');

  const hasClasses = classes.length > 0;
  const isSingle = selectedClasses.length === 1;
  const summary = isSingle ? classLabel(selectedClasses[0]) : `${selectedClasses.length} Classes`;

  const selectedIds = useMemo(() => new Set(selectedClasses.map(cls => cls.id)), [selectedClasses]);

  const visibleClasses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return classes;
    return classes.filter(cls => classLabel(cls).toLowerCase().includes(term));
  }, [classes, search]);

  const currentIds = useMemo(() => selectedClasses.map(cls => cls.id), [selectedClasses]);

  const handleShow = () => {
    openedWith.current = currentIds;
    openPanelBesideNav(overlay, tooltipId);
  };

  const handleHide = () => {
    hidePanelUntilPlaced(overlay);
    setSearch('');

    const changeType = getClassChangeType(openedWith.current, currentIds);
    if (!changeType) return;

    posthog?.capture('class_filter_changed', {
      previous_class_selection: getClassSelectionScope(openedWith.current.length, classes.length),
      class_change_type: changeType,
    });
  };

  const selectOnly = (classId: string) => select(classes.length === 1 ? [] : [classId]);

  const toggle = (classId: string) => {
    const isSelected = selectedIds.has(classId);

    if (isSelected && selectedIds.size === 1) return;

    const nextIds = isSelected
      ? selectedClasses.filter(cls => cls.id !== classId).map(cls => cls.id)
      : [...selectedClasses.map(cls => cls.id), classId];

    select(nextIds.length === classes.length ? [] : nextIds);
  };

  const selectAll = () => select([]);

  const clear = () => select(selectedClasses.length ? [selectedClasses[0].id] : []);

  if (!hasClasses) {
    return (
      <div className="nc_class-filter nc_class-filter--empty" data-testid="globalClassFilterEmpty">
        <ClassBadge variant="empty" />
        <span className="nc_class-filter__name">No classes yet</span>
      </div>
    );
  }

  const tooltipId = 'classSelectorBtn';

  return (
    <>
      <button
        type="button"
        id={tooltipId}
        className="nc_class-filter"
        data-testid="globalClassFilter"
        aria-haspopup="dialog"
        aria-label={`Class filter: ${isSingle ? summary : selectedClasses.map(classLabel).join(', ')}`}
        onClick={event => overlay.current?.toggle(event)}
      >
        <ClassBadge variant={isSingle ? 'class' : 'multi'} icon={isSingle ? selectedClasses[0].icon : null} />
        <span className="nc_class-filter__name">{summary}</span>
        <span className="nc_class-filter__count">{studentsLabel(selectedStudentCount)}</span>
        <ChevronsUpDown size={16} className="nc_class-filter__chevron" aria-hidden="true" />
      </button>

      {!isSingle && (
        <Tooltip target={`#${tooltipId}`} position="right">
          <div className="nc_class-filter__tooltip">
            {selectedClasses.map(cls => (
              <div key={cls.id}>{classLabel(cls)}</div>
            ))}
          </div>
        </Tooltip>
      )}

      <Tooltip target=".nc_class-filter__action--locked" position="left" content="1 class has to be selected" />

      <OverlayPanel ref={overlay} className="nc_class-filter__panel" onShow={handleShow} onHide={handleHide}>
        <div className="nc_class-filter__search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            id="classFilterSearch"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search class name"
            aria-label="Search class name"
          />
        </div>

        <ul className="nc_class-filter__options">
          {visibleClasses.map(cls => {
            const isSelected = selectedIds.has(cls.id);
            const isLocked = isSelected && selectedIds.size === 1;

            return (
              <li key={cls.id} className={`nc_class-filter__row ${isSelected ? 'nc_class-filter__row--selected' : ''}`}>
                <button
                  type="button"
                  id="classFilterClassName"
                  className="nc_class-filter__row-name"
                  aria-pressed={isSelected}
                  onClick={() => selectOnly(cls.id)}
                >
                  {isSelected ? (
                    <Check size={16} className="nc_class-filter__check" aria-hidden="true" />
                  ) : (
                    <ClassBadge variant="class" icon={cls.icon} size="sm" />
                  )}
                  <span className="nc_class-filter__option-name">{classLabel(cls)}</span>
                </button>

                <button
                  type="button"
                  id="classFilterAddRemoveButton"
                  className={`nc_class-filter__action ${isLocked ? 'nc_class-filter__action--locked' : ''}`}
                  aria-disabled={isLocked}
                  aria-label={`${isSelected ? 'Remove' : 'Add'} ${classLabel(cls)}`}
                  onClick={() => toggle(cls.id)}
                >
                  {isSelected ? <Minus size={16} aria-hidden="true" /> : <Plus size={16} aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="nc_class-filter__footer">
          {selectedClasses.length > 1 ? (
            <button type="button" className="nc_class-filter__bulk" onClick={clear}>
              Clear
            </button>
          ) : (
            <button type="button" id="classFilterSelectAllButton" className="nc_class-filter__bulk" onClick={selectAll}>
              Select all
            </button>
          )}
          <button type="button" className="nc_class-filter__done" onClick={() => overlay.current?.hide()}>
            Done
          </button>
        </div>
      </OverlayPanel>
    </>
  );
};

export default GlobalClassFilter;
